import { SearchXIcon } from "lucide-react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { EmptyState, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DemandCompany } from "@/types";

interface DemandCompaniesTableProps {
  companies: DemandCompany[];
  isDemandCompaniesPending: boolean;
  isDemandCompaniesError: boolean;
}

// TODO: STF-369: Perhaps in the future we will use a custom table component for this page rather than TanStack table.
const demandColumns: ColumnDef<DemandCompany>[] = [
  {
    accessorKey: "name",
    header: "Company name",
    cell: ({ row }) => <Text>{row.original.name}</Text>,
  },
];

export const DemandCompaniesTable = ({
  companies,
  isDemandCompaniesPending,
  isDemandCompaniesError,
}: DemandCompaniesTableProps) => {
  const table = useReactTable({
    data: companies,
    columns: demandColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => `${row.name}-${String(index)}`,
  });

  const rows = table.getRowModel().rows;

  const renderTableBody = () => {
    if (isDemandCompaniesPending) {
      return (
        <TableCell colSpan={demandColumns.length} className="h-24">
          <LoadingIndicator variant="medium" />
        </TableCell>
      );
    }
    if (isDemandCompaniesError) {
      return (
        <TableCell colSpan={demandColumns.length} className="h-24">
          <Row className="justify-center">
            <ErrorText errorMessage="Unable to load companies." />
          </Row>
        </TableCell>
      );
    }
    if (rows.length === 0) {
      return (
        <TableCell colSpan={demandColumns.length}>
          <EmptyState
            Icon={SearchXIcon}
            title="No results"
            description="No companies found matching your search."
          />
        </TableCell>
      );
    }
    return rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <Row className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-slate-50 [&_tr]:border-b-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className="sticky-border-bottom" key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="overflow-y-auto">{renderTableBody()}</TableBody>
      </Table>
    </Row>
  );
};
